import type { CustomerProfile } from "@tdelivr/api-client/generated/customer/customer-profile.js";
import type { Trip } from "@tdelivr/api-client/generated/trip/trip.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { absent, at, field, type RecordDetail } from "./record-detail.ts";

/**
 * What the lookup board does to the two listings it reads, away from any rendering.
 *
 * **It is one board over two services, which is the one fan-out Document 17 WEB-0743 allows.** That
 * document tolerates one listing per screen; this screen is *defined* as a search across record
 * kinds — "Find a customer, driver, trip, order or payment by any ID or phone number" — so a single
 * listing could not be this page at all. Two are read, each once, and both are tenant-scoped reads
 * the token already authorises. Nothing is joined: a row is a customer or a trip and says which,
 * which is exactly the design's `Type` column.
 *
 * **The search is the absence, not the rows.** The design's field takes "ID, phone or email" and
 * **nothing on the platform resolves a phone number or an email address to a record.** CUS-01
 * publishes `displayName` and `identitySubjectId` and no contact field at all; its listing filters
 * on `status` and `identitySubjectId` and nothing else. `docs/design/DESIGN_QUESTIONS.md` already
 * records this against this screen. So the field filters the page it has rather than querying one
 * it cannot, and the board says which it is doing. A search box that silently matched nothing
 * because the field it needed does not exist would read as "no such customer".
 *
 * **Three of the design's five chips are drawn disabled.** `customer` and `trip` have a
 * tenant-scoped collection read; `driver`, `order` and `payment` do not. DRV-01's roster read is
 * the dispatch console's and needs a presence join this screen has no reason to make; nothing
 * publishes a tenant-scoped order listing or payment listing a support console could call. A chip
 * that filtered to nothing would say the tenant has no drivers.
 *
 * **`Last activity` has no source and is drawn as what is published.** The design writes
 * `today 12:40`, `in trip`, `on a job` — a mix of an instant and a state, which is itself a clue
 * that the design is reading something this platform does not publish. `updatedAt` is when the
 * record last changed, which for a customer is when somebody edited their profile and not when
 * they last travelled. The column draws it and labels it `Last changed`, in the design's position
 * and not under the design's word, because the design's word would be false. R-35.
 *
 * **Opening a record is audited, the design says, and nothing here audits it.** The page's purpose
 * ends "Opening a record is audited." AUD-01 publishes an audit read and no audit *write* a console
 * can call — every ingestion route is `/internal/v1`. So a row opening on this board records
 * nothing, and the design's sentence is kept because it is the product's rule. This is the gap on
 * this screen most worth closing, and it is a different kind from the others: the others leave a
 * column thin, this one leaves a promise unkept.
 */

/**
 * How many of each kind to take in one request.
 *
 * Both listings are cursor-paged and both cap at 200. The board holds two windows rather than one,
 * which is what makes the count under it worth stating twice.
 */
export const PER_FETCH = 200;

/** The record kinds this board can actually search, and the ones the design draws and cannot. */
export const KINDS = Object.freeze([
  { id: "customer", label: "customer", searchable: true },
  { id: "trip", label: "trip", searchable: true },
  {
    id: "driver",
    label: "driver",
    searchable: false,
    because:
      "DRV-01's roster read belongs to the dispatch console and needs a presence join this screen has no reason to make; nothing publishes a driver listing a support console could call on its own.",
  },
  {
    id: "order",
    label: "order",
    searchable: false,
    because:
      "Nothing publishes a tenant-scoped order listing. DEL-03's in-flight set is the deliveries board's, and a support agent looking up an order by its identifier has no read to make.",
  },
  {
    id: "payment",
    label: "payment",
    searchable: false,
    because:
      "Nothing publishes a payment listing. PAY-01's reads are by identifier and the identifier is what this screen exists to find.",
  },
] as const);

export type KindId = (typeof KINDS)[number]["id"];

/**
 * One row of this board, whichever kind it came from.
 *
 * A shaped row rather than a union the component destructures, for one reason: the design's grid is
 * three columns of text and a badge, and a component that switched on the kind per cell would be
 * three components in one. The shaping happens here, where each field can carry a note about where
 * it came from.
 */
export interface LookupRow {
  readonly key: string;
  readonly kind: KindId;
  /** The design's `Record` cell. */
  readonly record: string;
  /** Whether `record` is an identifier rather than a name, which decides the mono face. */
  readonly recordIsIdentifier: boolean;
  /** The design's `Type` cell: the kind, then what the design puts after it. */
  readonly type: string;
  /** The published instant the design's `Last activity` column sits over. */
  readonly changedAt: string;
  readonly state: { readonly classKey: StateClassKey; readonly label: string };
  /** The owning service, for the badge and the panel. */
  readonly source: string;
  /** The contract's own status word, for the badge's title. */
  readonly status: string;
  /** Everything the panel draws, which is only what the listing answered. */
  readonly detail: RecordDetail;
}

/**
 * The state class and the word for a customer, from `status`.
 *
 * | status            | class     | word              |
 * | ----------------- | --------- | ----------------- |
 * | `active`          | `done`    | `active`          |
 * | `restricted`      | `caution` | `restricted`      |
 * | `suspended`       | `failed`  | `suspended`       |
 * | `closure_pending` | `human`   | `closing`         |
 * | `closed`          | `closed`  | `closed`          |
 * | `merged`          | `closed`  | `merged`          |
 *
 * **`closure_pending` is `human`**, because it is the one status on this list where somebody has to
 * do something: the design's own queue draws `Account deletion request · human · needs a
 * supervisor`, and a closure in progress is that request mid-flight. `active` is `done` rather than
 * `active` — the design's `active` class means "a thing is happening now", and an active account is
 * a settled state, which is what its `done` class is for.
 */
function customerState(profile: CustomerProfile): { classKey: StateClassKey; label: string } {
  switch (profile.status) {
    case "active":
      return { classKey: "done", label: "active" };
    case "restricted":
      return { classKey: "caution", label: "restricted" };
    case "suspended":
      return { classKey: "failed", label: "suspended" };
    case "closure_pending":
      return { classKey: "human", label: "closing" };
    case "closed":
      return { classKey: "closed", label: "closed" };
    case "merged":
      return { classKey: "closed", label: "merged" };
  }
}

/**
 * The state class and the word for a trip, from `status`.
 *
 * The design's own row for a trip reads `trip · Westlands → JKIA · in trip · active`, so a running
 * trip is `active` and the words are the contract's with their underscores opened out — which is
 * what the dispatch console's active-rides board already does for the same enum.
 */
function tripState(trip: Trip): { classKey: StateClassKey; label: string } {
  switch (trip.status) {
    case "assigned":
      return { classKey: "idle", label: "assigned" };
    case "driver_en_route":
      return { classKey: "active", label: "driver en route" };
    case "driver_arrived":
      return { classKey: "active", label: "driver arrived" };
    case "waiting":
      return { classKey: "waiting", label: "waiting" };
    case "in_trip":
      return { classKey: "active", label: "in trip" };
    case "completed":
      return { classKey: "done", label: "completed" };
    case "cancelled":
      return { classKey: "closed", label: "cancelled" };
    case "no_show":
      return { classKey: "failed", label: "no show" };
  }
}

/**
 * A customer, as a row of this board.
 *
 * `displayName` is a real name — CUS-01 publishes one, which is more than SUP-01 does — so the
 * `Record` cell is the name and the identifier goes to the panel. That is the one place on this
 * console where the design's first column can be drawn as the design draws it.
 *
 * The design's second cell is `customer · 182 trips`. **Nothing publishes a trip count per
 * customer**, and TRP-01's listing is not scoped by rider, so counting would mean paging the
 * tenant's trips and tallying — a number that would be wrong the moment it was drawn. The cell is
 * the kind and the customer's locale, which is published and is the other thing an agent about to
 * write to somebody needs.
 */
export function rowForCustomer(profile: CustomerProfile): LookupRow {
  const state = customerState(profile);
  return {
    key: `customer:${profile.id}`,
    kind: "customer",
    record: profile.displayName,
    recordIsIdentifier: false,
    type: `customer · ${profile.locale}`,
    changedAt: profile.updatedAt,
    state,
    source: "CUS-01",
    status: profile.status,
    detail: {
      headline: profile.displayName,
      identifier: profile.id,
      source: "CUS-01",
      state: { ...state, code: "CUS-01", status: profile.status },
      fields: [
        field("Kind", "customer"),
        field("Locale", profile.locale, true),
        field("Identity subject", profile.identitySubjectId, true),
        absent(
          "Phone",
          "CUS-01 publishes no contact field at all — no phone, no email. The design draws a masked phone number and a reveal that asks for a reason. See R-35.",
        ),
        absent(
          "Trips",
          "Nothing publishes a trip count for a customer, and TRP-01's listing is not scoped by rider, so a count would mean paging the tenant's trips. See R-35.",
        ),
        field("Registered", at(profile.createdAt) ?? profile.createdAt),
        field("Last changed", at(profile.updatedAt) ?? profile.updatedAt),
        field("Version", `v${String(profile.version)}`),
      ],
      note:
        "The design says opening a record is audited. AUD-01 publishes an audit read and no audit " +
        "write a console can call — every ingestion route is /internal/v1 — so opening this row " +
        "recorded nothing. See R-35.",
    },
  };
}

/**
 * A trip, as a row of this board.
 *
 * The design's cell is `trip · Westlands → JKIA` — the two ends of the journey. **TRP-01 publishes
 * no addresses and no coordinates**: a trip carries the identifiers of the request, the quote and
 * the assignment it came from, and the places live on MOB-03's quote, which this screen has no
 * reason and no route to fan out to. So the `Record` cell is the trip identifier in mono and the
 * `Type` cell says what state it is in, which is what the design's own `in trip` second line says
 * anyway.
 */
export function rowForTrip(trip: Trip): LookupRow {
  const state = tripState(trip);
  return {
    key: `trip:${trip.tripId}`,
    kind: "trip",
    record: trip.tripId,
    recordIsIdentifier: true,
    type: `trip · ${state.label}`,
    changedAt: trip.updatedAt,
    state,
    source: "TRP-01",
    status: trip.status,
    detail: {
      headline: trip.tripId,
      identifier: trip.tripId,
      source: "TRP-01",
      state: { ...state, code: "TRP-01", status: trip.status },
      fields: [
        field("Kind", "trip"),
        field("Rider", trip.riderId, true),
        field("Driver", trip.driverId, true),
        absent(
          "Route",
          "TRP-01 publishes no addresses and no coordinates. The places live on MOB-03's quote, which this screen has no route to and would be a second service on one row. See R-35.",
        ),
        field("Request", trip.requestId, true),
        field("Quote", trip.quoteId, true),
        field("Assignment", trip.assignmentId, true),
        field("Started", at(trip.createdAt) ?? trip.createdAt),
        field("Last changed", at(trip.updatedAt) ?? trip.updatedAt),
        field("Version", `v${String(trip.version)}`),
      ],
      note:
        "The design says opening a record is audited. AUD-01 publishes an audit read and no audit " +
        "write a console can call — every ingestion route is /internal/v1 — so opening this row " +
        "recorded nothing. See R-35.",
    },
  };
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type LookupOrder = "default" | "state" | "az" | "za";

export interface LookupQuery {
  readonly search: string;
  readonly order: LookupOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface LookupView {
  readonly rows: readonly LookupRow[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly LookupRow[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(row: LookupRow): string {
  return [row.record, row.type, row.status, row.state.label, row.detail.identifier]
    .concat(row.detail.fields.filter((entry) => entry.absent !== true).map((entry) => entry.value))
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is most recently changed first, across both kinds. Neither service's own order survives
 * being merged with the other's — CUS-01 and TRP-01 each page their own set — so the one ordering
 * that is true of the combined list is the published instant on each row. That is a choice this
 * screen makes and says so: `changedAt` is `updatedAt`, which is when the record changed and not
 * when the person was last active, which is what the design's column means.
 */
export function lookupPage(
  rows: readonly LookupRow[],
  query: LookupQuery,
  kind?: KindId,
): LookupView {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (row) =>
      (kind === undefined || row.kind === kind) &&
      (needle.length === 0 || searchableText(row).includes(needle)),
  );

  const ordered = [...matched].sort((left, right) => {
    if (query.order === "state") return left.state.label.localeCompare(right.state.label);
    if (query.order === "az") return left.record.localeCompare(right.record);
    if (query.order === "za") return right.record.localeCompare(left.record);
    return right.changedAt.localeCompare(left.changedAt);
  });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The sentence this board owes an agent, which on this screen is three claims rather than one.
 *
 * It says how much of each kind was fetched, that the search covers those rows rather than asking a
 * service, and that no field anywhere resolves a phone number or an email address — which is what
 * an agent typing one into the box will otherwise conclude is a record that does not exist.
 */
export function fetchedNote(
  customers: number | undefined,
  trips: number | undefined,
  more: boolean,
): string | undefined {
  if (customers === undefined && trips === undefined) return undefined;
  const windows = [
    customers === undefined ? undefined : `${String(customers)} customers`,
    trips === undefined ? undefined : `${String(trips)} trips`,
  ]
    .filter((part): part is string => part !== undefined)
    .join(" and ");
  return (
    `This board holds ${windows} — two windows, not the tenant's totals, because CUS-01 and ` +
    `TRP-01 each publish a cursor and no count. ` +
    `Typing a phone number or an email address will match nothing: no field on any contract ` +
    `resolves either to a record, so the box filters the rows above rather than asking a service. ` +
    (more
      ? `Next fetches more of both, each from its own cursor.`
      : `Both services answered no cursor, so this is all of either.`)
  );
}

/**
 * The record kinds present in what came back, which is what this board's state filter offers.
 *
 * Every other board on this console filters by state; this one filters by kind, because a board
 * holding customers and trips has two state vocabularies and a dropdown mixing `active` (a
 * customer) with `in trip` (a trip) would offer a filter that means two things. The kind is the one
 * axis every row here shares.
 *
 * Built from the rows rather than from `KINDS`, the way the design's own `gridUI` builds its state
 * list — `[...new Set(items.map(x => x.state))]` — so a filter never offers a kind the board has
 * none of.
 */
export function kindsPresent(rows: readonly LookupRow[]): readonly string[] {
  return [...new Set(rows.map((row) => row.kind))];
}
